import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qualityService, AuditScheduleRecord } from '../../services/quality.service';
import employeeService, { employeeKeys } from '@/services/employee.service';
import { buildEmployeeIdToLabelMap, resolveEmployeeDisplayFromMap } from '@/utils/userDisplay';

const { Title } = Typography;
const { Search } = Input;

const AuditSchedulesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['quality', 'audit-schedules'],
    queryFn: () => qualityService.getAuditSchedules(),
    staleTime: 60_000,
  });

  const { data: employees = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });
  const employeeDisplayMap = React.useMemo(
    () =>
      buildEmployeeIdToLabelMap(
        employees as Array<{ id: string; first_name?: string; last_name?: string; full_name?: string; employee_number?: string }>
      ),
    [employees]
  );

  const createMutation = useMutation({
    mutationFn: (data: Partial<AuditScheduleRecord>) => qualityService.createAuditSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'audit-schedules'] });
      message.success('Audit scheduled successfully');
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save audit schedule');
    },
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Scheduled: 'blue',
      'In Progress': 'processing',
      Completed: 'success',
      Cancelled: 'error',
      Postponed: 'warning',
    };
    return colors[status] || 'default';
  };

  const columns: ColumnsType<AuditScheduleRecord> = [
    {
      title: 'Schedule Number',
      dataIndex: 'schedule_number',
      key: 'schedule_number',
    },
    {
      title: 'Audit Title',
      dataIndex: 'audit_title',
      key: 'audit_title',
    },
    {
      title: 'Type',
      dataIndex: 'audit_type',
      key: 'audit_type',
    },
    {
      title: 'Start Date',
      dataIndex: 'scheduled_start_date',
      key: 'scheduled_start_date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Lead auditor',
      dataIndex: 'lead_auditor_id',
      key: 'lead_auditor_id',
      render: (id: string) => {
        const e = employees.find((x: { id: string }) => x.id === id);
        if (!e) return resolveEmployeeDisplayFromMap(employeeDisplayMap, id, 'Unknown employee');
        return `${(e as any).employee_number || ''} ${(e as any).first_name || ''} ${(e as any).last_name || ''}`.trim();
      },
    },
  ];

  const handleOpen = () => {
    form.resetFields();
    form.setFieldsValue({
      audit_type: 'Internal',
      scheduled_start_date: dayjs(),
      scheduled_end_date: dayjs(),
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    await createMutation.mutateAsync({
      audit_title: values.audit_title,
      audit_type: values.audit_type,
      scheduled_start_date: values.scheduled_start_date?.format?.('YYYY-MM-DD'),
      scheduled_end_date: values.scheduled_end_date?.format?.('YYYY-MM-DD'),
      lead_auditor_id: values.lead_auditor_id,
    });
  };

  const filtered = schedules.filter((s) =>
    s.audit_title?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Audit Schedules</Title>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="Search audit schedules..."
            allowClear
            style={{ width: 300 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
            Schedule Audit
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          rowKey="id"
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title="Schedule Audit"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="audit_title" label="Audit title" rules={[{ required: true }]}>
            <Input placeholder="e.g. Internal QMS audit" />
          </Form.Item>
          <Form.Item name="audit_type" label="Audit type" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="Internal">Internal</Select.Option>
              <Select.Option value="External">External</Select.Option>
              <Select.Option value="Supplier">Supplier</Select.Option>
              <Select.Option value="Customer">Customer</Select.Option>
              <Select.Option value="Regulatory">Regulatory</Select.Option>
              <Select.Option value="Certification">Certification</Select.Option>
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="scheduled_start_date" label="Start date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="scheduled_end_date" label="End date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="lead_auditor_id" label="Lead auditor" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Select employee"
              optionFilterProp="label"
              options={employees.map((e: { id: string; first_name?: string; last_name?: string; employee_number?: string }) => ({
                value: e.id,
                label: `${e.employee_number || ''} ${e.first_name || ''} ${e.last_name || ''}`.trim() || e.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AuditSchedulesPage;
